import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

import ApplicationFooter from '@frolight/components/ApplicationFooter';
import Button from '@frolight/components/Button';

import * as usersService from '@frolight/services/api/usersService';

import HTTPStatusError from '@frolight/utils/HTTPStatusError';

import useGatewayRegistrationRequestsForUserData from './hooks/useGatewayRegistrationRequestsForUserData';
import useUnregisteredGatewaysForUserData from './hooks/useUnregisteredGatewaysForUserData';
import useRegisterGatewaysToOrganisation from './hooks/useRegisterGatewaysToOrganisation';
import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';
import useAssignUserToOrganisation from './hooks/useAssignUserToOrganisation';
import useUnverifiedUserData from './hooks/useUnverifiedUserData';
import useCreateOrganisation from './hooks/useCreateOrganisation';
import LocaleSelector from '@frolight/components/LocaleSelector';
import usePageTitle from '@frolight/hooks/usePageTitle';
import useAuth from '@frolight/hooks/useAuth';
import useSignIn from './hooks/useSignIn';

const AccountCreationCompletion = () => {
  const { lftext } = useLocalizedFormattedText();
  
  usePageTitle(lftext('pages.device_registration.account_creation_completion.page_title'));

  const { verificationToken } = useParams();
  
  const { auth, setAuth } = useAuth();

  // Form data
  const [ formPassword, setFormPassword ] = useState('');
  const [ formOrganisationName, setFormOrganisationName ] = useState('');

  const [ accountCreationComplete, setAccountCreationComplete ] = useState(false);

  // 1. Get the unverified user data according by the verificationToken
  const {
    loading: unverifiedUserDataLoading,
    error: unverifiedUserDataError,
    data: unverifiedUserData
  } = useUnverifiedUserData(verificationToken);
  // 2. Get the gateway registration requests for the unverified user
  const {
    loading: gatewayRegistrationRequestsForUserDataLoading,
    error: gatewayRegistrationRequestsForUserDataError,
    data: gatewayRegistrationRequestsForUserData
  } = useGatewayRegistrationRequestsForUserData(unverifiedUserData?.id);
  // 3. Get the gateway information for all registration requests
  const {
    loading: unregisteredGatewaysForUserDataLoading,
    error: unregisteredGatewaysForUserDataError,
    data: unregisteredGatewaysForUserData
  } = useUnregisteredGatewaysForUserData(gatewayRegistrationRequestsForUserData);

  // 4. Wait for user to submit the form
  const [ formLoading, setFormLoading ] = useState(false);
  const [ formError, setFormError ] = useState(null);
  const [ verifiedUser, setVerifiedUser ] = useState(null);

  // 5. Sign in after user has been verified
  const {
    loading: signInLoading,
    error: signInError,
    data: signInResponse
  } = useSignIn(verifiedUser?.email, formPassword);

  // 6. Set 'auth' after sign in
  useEffect(() => {
    if (signInResponse) {
      if (!signInResponse.access_token) {
        throw new Error('No access token received');
      }

      const decodedToken = jwtDecode(signInResponse.access_token);
      setAuth({
        user: {
          id: decodedToken.sub,
          firstName: decodedToken.given_name,
          lastName: decodedToken.family_name,
          email: decodedToken.email,
          avatar: decodedToken.picture
        },
        memberships: decodedToken.memberships,
        roles: decodedToken.roles,
        accessToken: {
          token: signInResponse.access_token,
          type: signInResponse.token_type
        }
      });
    }
  }, [ signInResponse ]);

  // 7. Now that the user is authenticated, create the organisation
  const {
    loading: createOrganisationLoading,
    error: createOrganisationError,
    data: createOrganisationData
  } = useCreateOrganisation(formOrganisationName, auth?.accessToken);
  // 8. Next, we assign the user to the organisation
  const {
    loading: assignUserToOrganisationLoading,
    error: assignUserToOrganisationError,
    data: assignUserToOrganisationData
  } = useAssignUserToOrganisation(verifiedUser?.id, createOrganisationData?.id);
  // 9. When user is assigned to org, we register all gateways with a registration request for this user to the newly created org
  const {
    loading: registerGatewaysToOrganisationLoading,
    error: registerGatewaysToOrganisationError,
    data: registerGatewaysToOrganisationData
  } = useRegisterGatewaysToOrganisation(unregisteredGatewaysForUserData, createOrganisationData?.id, assignUserToOrganisationData !== null);
  // 10. All done
  useEffect(() => {
    let mounted = true;
    if (
      !isNaN(assignUserToOrganisationData) &&
      parseInt(assignUserToOrganisationData) === 1 &&
      registerGatewaysToOrganisationData &&
      gatewayRegistrationRequestsForUserData &&
      registerGatewaysToOrganisationData.length === gatewayRegistrationRequestsForUserData.length
    ) {
      if (mounted) setAccountCreationComplete(true);
    }
    return () => {
      mounted = false;
    }
  }, [ assignUserToOrganisationData, registerGatewaysToOrganisationData, gatewayRegistrationRequestsForUserData ]);

  // 4. Verify user
  const onFormSubmit = async (e) => {
    setFormLoading(true);

    if (e) {
      e.preventDefault();
    }

    try {
      const verifyUserResponse = await usersService.verifyUser(
        new AbortController(),
        {
          email: unverifiedUserData.email,
          password: formPassword,
          verificationToken: verificationToken
        }
      );
      if (!verifyUserResponse.data) {
        throw new Error('Error verifying user');
      }
      setVerifiedUser(verifyUserResponse.data);
    } catch (err) {
      setFormError(err);
    } finally {
      setFormLoading(false);
    }
  };

  const validateForm = () => {
    return (
      formPassword && formPassword.length > 0
      && formOrganisationName && formOrganisationName.length
    );
  }
  
  const renderGeneralError = (errorType = null) => {
    let errorTitle;
    let errorDescription;

    switch (errorType) {
      case 'GET_UNVERIFIED_USER_DATA':
        if (unverifiedUserDataError.errorCode === 404) {
          error= lftext('pages.device_registration.account_creation_completion.unverified_user_data.is_404_error', { verificationToken:verificationToken });
        } else {
          error = lftext('pages.device_registration.account_creation_completion.unverified_user_data.not_404_error');
        }
        break;
      case 'GET_GATEWAY_REGISTRATION_REQUESTS_FOR_USER':
        error = lftext('pages.device_registration.account_creation_completion.gateway_registration');
        break;
      case 'GET_UNREGISTERED_GATEWAYS_FOR_USER':
        error = lftext('pages.device_registration.account_creation_completion.unregistered_gateways_error');
        break;
      case 'SIGN_IN_ERROR':
        error = lftext('pages.device_registration.account_creation_completion.sign_in_error');
        break;
      case 'CREATE_ORGANISATION':
        error = lftext('pages.device_registration.account_creation_completion.create_organisation');
        break;
      case 'ASSIGN_USER_TO_ORGANISATION':
        error = lftext('pages.device_registration.account_creation_completion.assign_user');
        break;
      case 'REGISTER_GATEWAYS_TO_ORGANISATION':
        error = lftext('pages.device_registration.account_creation_completion.register_gateways');
        break;
      default:
        error = lftext('pages.device_registration.account_creation_completion.default_error');
        break;
    }
    return (
      <div className="feedback_message  feedback_message--error">
        {
          !!errorTitle && { error }
        }
        <p>
          { lftext('pages.device_registration.account_creation_completion.message') }
        </p>
      </div>
    );
  }

  const renderFormError = () => {
    let error
    if (formError instanceof HTTPStatusError) {
      if (formError.errorCode === 401) {
        error = lftext('pages.device_registration.account_creation_completion.401_error');
      }
    }

    return (
      <div className="form__feedback">
        <div className="feedback_message  feedback_message--error">
          {!!error && <p>{ error }</p>}
          <p>
            { lftext('pages.device_registration.account_creation_completion.contact_us_message') }
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="simple_page">
      <div className="simple_page__inner">
        <LocaleSelector theme='dark' />
        <section className="panel  panel--huge">
          <div className="panel__header">
            <header className="small_application_header">
              <Link className="logo_link  logo_link--large" to="/">
                <img className="logo_link__img" src="images/frolight_logo.svg" alt="Frolight logo" />
              </Link>
            </header>
          </div>
          <div className="panel__content">
            <main>
              {
                (
                  (
                    unverifiedUserDataLoading ||
                    gatewayRegistrationRequestsForUserDataLoading ||
                    unregisteredGatewaysForUserDataLoading ||
                    signInLoading ||
                    createOrganisationLoading ||
                    assignUserToOrganisationLoading ||
                    registerGatewaysToOrganisationLoading
                  ) && (
                    !unverifiedUserDataError &&
                    !gatewayRegistrationRequestsForUserDataError &&
                    !unregisteredGatewaysForUserDataError &&
                    !signInError &&
                    !createOrganisationError &&
                    !assignUserToOrganisationError &&
                    !registerGatewaysToOrganisationError
                  )
                ) &&
                <>
                  <div className="loader">
                    <p>
                      { lftext('common.loading') }
                    </p>
                  </div>
                </>
              }
              {
                !unverifiedUserDataLoading &&
                unverifiedUserDataError &&
                renderGeneralError('GET_UNVERIFIED_USER_DATA')
              }
              {
                !unverifiedUserDataError &&
                !gatewayRegistrationRequestsForUserDataLoading &&
                gatewayRegistrationRequestsForUserDataError &&
                renderGeneralError('GET_GATEWAY_REGISTRATION_REQUESTS_FOR_USER')
              }
              {
                !unverifiedUserDataError &&
                !gatewayRegistrationRequestsForUserDataError &&
                !unregisteredGatewaysForUserDataLoading &&
                unregisteredGatewaysForUserDataError &&
                renderGeneralError('GET_UNREGISTERED_GATEWAYS_FOR_USER')
              }
              {
                unregisteredGatewaysForUserData &&
                !verifiedUser &&
                <form
                  className="form  form--account_creation_completion"
                  onSubmit={ onFormSubmit }
                  method="post"
                >
                  <div className="form__title">
                    <h1>
                      { lftext('pages.device_registration.account_creation_completion.form.title') }
                    </h1>
                  </div>
                  <div className="form__instructions">
                    <p>
                      { lftext('pages.device_registration.account_creation_completion.form.instructions') }
                    </p>
                  </div>
                  {
                    !formLoading &&
                    formError && 
                    renderFormError()
                  }
                  <fieldset className="form__group">
                    <h2>{lftext('pages.device_registration.account_creation_completion.form.organisation_title')}</h2>
                    <div className="form__field">
                      <div className="text_input">
                        <label
                          className="text_input__label"
                          htmlFor="account_creation_completion-organisation_name"
                        >
                          { lftext('pages.device_registration.account_creation_completion.form.organisation_name') }
                        </label>
                        <input
                          id="account_creation_completion-organisation_name"
                          className="text_input__field"
                          type="text"
                          name="organisation"
                          required
                          disabled={ formLoading }
                          value={ formOrganisationName }
                          onChange={ (e) => setFormOrganisationName(e.target.value) }
                        />
                      </div>
                    </div>
                  </fieldset>
                  <fieldset className="form__group">
                    <div className="form__field">
                      <div className="text_input">
                        <label
                          className="text_input__label"
                          htmlFor="account_creation_completion-password"
                        >
                          { lftext('pages.device_registration.account_creation_completion.form.password_label') }
                        </label>
                        <input
                          id="account_creation_completion-password"
                          className="text_input__field"
                          type="password"
                          name="password"
                          required
                          autoFocus
                          disabled={ formLoading }
                          value={ formPassword }
                          onChange={ (e) => setFormPassword(e.target.value) }
                        />
                      </div>
                    </div>
                  </fieldset>
                  {
                    unregisteredGatewaysForUserData.length > 0 &&
                    <fieldset className="form__group">
                      <div className="rich_text">
                        <p>
                          {lftext('pages.device_registration.account_creation_completion.unregistered_gateways')}
                        </p>
                        <ul>
                          {
                            unregisteredGatewaysForUserData.map((gateway) => {
                              return (
                                <li key={ `gateway-${gateway.id}` }>{ gateway.name }</li>
                              );
                            })
                          }
                        </ul>
                      </div>
                    </fieldset>
                  }
                  <fieldset className="form__group">
                    <div className="form__actions">
                      <Button
                        type="submit"
                        className="button"
                        label={ lftext('pages.device_registration.account_creation_completion.form.submit') }
                        priority="primary"
                        disabled={ formLoading || !validateForm() }
                      />
                    </div>
                  </fieldset>
                </form>
              }
              {
                !signInLoading &&
                signInError &&
                renderGeneralError('SIGN_IN_ERROR')
              }
              {
                !signInLoading &&
                !signInError &&
                !createOrganisationLoading &&
                createOrganisationError &&
                renderGeneralError('CREATE_ORGANISATION')
              }
              {
                !signInLoading &&
                !signInError &&
                !assignUserToOrganisationLoading &&
                assignUserToOrganisationError &&
                renderGeneralError('ASSIGN_USER_TO_ORGANISATION')
              }
              {
                !signInLoading &&
                !signInError &&
                !createOrganisationLoading &&
                !createOrganisationError &&
                !assignUserToOrganisationLoading &&
                !assignUserToOrganisationError &&
                !registerGatewaysToOrganisationLoading &&
                registerGatewaysToOrganisationError &&
                renderGeneralError('REGISTER_GATEWAYS_TO_ORGANISATION')
              }
              {
                accountCreationComplete &&
                <div className="rich_text">
                  <h1>
                    { lftext('pages.device_registration.account_creation_completion.ready_message') }
                  </h1>
                  {lftext('pages.device_registration.account_creation_completion.ready_message_link')}
                </div>
              }
            </main>
          </div>
        </section>
      </div>
      <div className="simple_page__footer">
        <ApplicationFooter compact={ false } />
      </div>
    </div>
  );
}

export default AccountCreationCompletion;
